import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Newsletter = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const texts = {
    it: {
      title: "Resta aggiornato",
      subtitle: "Iscriviti per ricevere consigli di viaggio esclusivi e offerte speciali",
      placeholder: "La tua email",
      button: "Iscriviti",
      success: "Iscrizione completata!",
      error: "Si è verificato un errore",
      exists: "Email già registrata"
    },
    en: {
      title: "Stay updated",
      subtitle: "Subscribe to receive exclusive travel tips and special offers",
      placeholder: "Your email",
      button: "Subscribe",
      success: "Subscription complete!",
      error: "An error occurred",
      exists: "Email already registered"
    }
  };

  const t = texts[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        language
      });
      
      if (response.data.status === 'exists') {
        setStatus('exists');
      } else {
        setStatus('success');
        setEmail('');
      }
    } catch (error) {
      setStatus('error');
    }

    setTimeout(() => setStatus('idle'), 3000);
  };

  return (
    <section className="py-24 bg-[#0A0A0A]" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-xl mx-auto text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.title}
          </h2>
          <p className="text-[#888] mb-8">
            {t.subtitle}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder={t.placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-12 px-5 rounded-full bg-[#1a1a1a] border border-[#333] text-white placeholder:text-[#666] focus:outline-none focus:border-[#C9B896] transition-colors"
              required
              disabled={status === 'loading'}
              data-testid="newsletter-email-input"
            />
            
            <button
              type="submit"
              disabled={status === 'loading'}
              className="h-12 px-6 rounded-full bg-[#C9B896] hover:bg-[#A89770] text-black font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="newsletter-submit-btn"
            >
              {status === 'loading' ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                  <Send className="w-4 h-4" />
                </motion.div>
              ) : (
                <>
                  {t.button}
                  <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {status !== 'idle' && status !== 'loading' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 flex items-center justify-center gap-2 text-sm"
            >
              {status === 'success' && (
                <>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">{t.success}</span>
                </>
              )}
              {status === 'error' && (
                <>
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400">{t.error}</span>
                </>
              )}
              {status === 'exists' && (
                <>
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400">{t.exists}</span>
                </>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
